// Shared renderer for the "Listing Type" column pill.
//
// One place decides how a listing type is coloured so every table that shows
// the column reads the same way:
//
//   Assignment           -> yellow
//   Presale / New Lease  -> system blue
//   Resale / Re-Lease    -> red
//
// The styles are global (`src/assets/styles/listing-type-pill.css`) rather than
// scoped: Tabulator formatters inject their markup outside the component's
// scoped-CSS boundary, so a `<style scoped>` rule never reaches them.
//
// Pure functions, no Vue / `this` dependencies.

export const LISTING_TYPE_PILL_CLASS = "rpd-lt-pill";

// Keyed on the normalised value (see `normalize`), so "Re-Lease", "re lease"
// and the "Release" filter label all land on the same variant.
const VARIANTS = {
  assignment: "yellow",
  presale: "blue",
  newlease: "blue",
  resale: "red",
  release: "red",
};

// jsPDF wants plain RGB triples, so the export path can't read the stylesheet.
// Keep these in step with listing-type-pill.css.
export const LISTING_TYPE_PDF_COLORS = {
  yellow: { fg: [138, 106, 0], bg: [253, 243, 220], border: [224, 168, 0] },
  blue: { fg: [11, 94, 215], bg: [232, 241, 255], border: [0, 123, 255] },
  red: { fg: [212, 56, 71], bg: [251, 233, 235], border: [212, 56, 71] },
  neutral: { fg: [84, 84, 84], bg: [240, 241, 244], border: [201, 204, 210] },
};

function normalize(value) {
  return String(value == null ? "" : value)
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

// Cell values come from the backend, and `listingTypePillHtml` hands its result
// straight to Tabulator as raw HTML.
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Colour variant for a raw listing type: "yellow" | "blue" | "red" | "neutral".
 * Unrecognised values fall back to "neutral" rather than borrowing a meaning.
 */
export function listingTypePillVariant(value) {
  return VARIANTS[normalize(value)] || "neutral";
}

/** Class string for template use, e.g. `:class="listingTypePillClass(row.type)"`. */
export function listingTypePillClass(value) {
  return `${LISTING_TYPE_PILL_CLASS} ${LISTING_TYPE_PILL_CLASS}--${listingTypePillVariant(value)}`;
}

/**
 * Pill markup for Tabulator `formatter` callbacks, which must return an HTML
 * string. Empty cells render `fallback` instead of an empty pill.
 */
export function listingTypePillHtml(value, fallback = '<span class="rpd-nil">&#8212;</span>') {
  const label = String(value == null ? "" : value).trim();
  if (!label) return fallback;
  return (
    `<span class="${listingTypePillClass(label)}">` +
    `<span class="${LISTING_TYPE_PILL_CLASS}__dot"></span>${escapeHtml(label)}</span>`
  );
}

/** RGB triples for the jsPDF export of the column. */
export function listingTypePdfColors(value) {
  return LISTING_TYPE_PDF_COLORS[listingTypePillVariant(value)];
}
